/* eslint-disable ngrx/prefer-effect-callback-in-block-statement */

import { Inject, Injectable, NgZone, DOCUMENT } from '@angular/core';
import { DefaultUrlSerializer, PRIMARY_OUTLET, Router, UrlTree } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Store } from '@ngrx/store';
import { fromEvent, of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';
import { EnvironmentService } from '../../../environments/environment.service';
import { ErrorMessage, ErrorType } from '../../domain/error/error-message.model';
import { isErrorMessage, manageError, ManageErrorSeverity } from '../../domain/error/error.util';
import { DottnetTemplate, TemplateCt } from '../../domain/template-ct/template-ct.model';
import { selectTemplateAll } from '../../domain/template-ct/template-ct.selectors';
import {
  onlyClient,
  onlyWhenOnline,
  onlyWhenReady
} from '../../shared/util/operators';
import { selectAuthLoginState } from '../auth/auth.selectors';
import { LogService } from '../log/log.service';
import { selectSessionID } from '../session/session.selectors';
import {
  saveClickLog,
  saveClickLogFailure,
  saveClickLogSuccess,
  saveClick,
  saveClickFailure,
  saveClickSuccess,
  saveContentClick
} from './click.actions';
import { ClickDetail, ClickLogID } from './click.models';
import { ClickService } from './click.service';
import { ROUTER_NAVIGATED, RouterNavigationAction } from '@ngrx/router-store';
import { setCurrentPage } from '../current-page/current-page.actions';
import { isNumber, replaceAll } from '../../shared/util/util';


//  click to external sites
@Injectable({ providedIn: 'root' })
export class ClickEffects {
  constructor(
    private logService: LogService,
    private router: Router,
    private store: Store,
    private environmentService: EnvironmentService,
    private clickService: ClickService,
    private actions$: Actions,
    // Document represents the rendering context. When switching to browser, it coincides with the DOM
    @Inject(DOCUMENT) private ssrDocument: Document,
    private ngZone: NgZone
  ) {
    // Built once: Router.config is fully populated before NgRx instantiates
    // effects, and the app never calls resetConfig. It is the same array as
    // app.routes.ts, so adding a route there updates this set automatically —
    // no second list to keep in sync.
    this.routableSegments = new Set(
      this.router.config
        .map((route) => route.path)
        .filter((path): path is string => path != null && path !== '**')
        .map((path) => path.split('/')[0]) // 'profile/activate' -> 'profile'
    );
    this.clientOrigin = new URL(this.environmentService.clientDomain).origin;
  }

  private readonly routableSegments: Set<string>;
  private readonly clientOrigin: string;

  saveClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClick),
      tap((action) => this.logService.info('Effect: saveClick ', action)),
      filter((payload) => !!payload.clickTo),
      onlyWhenReady(this.store),
      concatLatestFrom(() => [
        this.store.select(selectSessionID),
        this.store.select(selectTemplateAll())
      ]),
      tap((event) => {
        this.logService.info('saveClick received event', event);
      }),

      map(([payload, sessionId, templateArray]) =>
        this.getClickParametersFromUrl(
          payload.clickTo,
          sessionId,
          templateArray,
          payload?.action ? payload.action : '',
          this.environmentService.clientDomain,
          payload?.context?.contextTypeID ? payload.context.contextTypeID : 0,
          payload?.context?.contextID ? payload.context.contextID : 0
        )
      ),
      mergeMap((clickDetail) => {
        if (isErrorMessage(clickDetail)) {
          this.logService.error('Error generating click detail: ', clickDetail);
          return of(saveClickFailure({ error: clickDetail }));
        } else {
          this.logService.debug('Click detail generated: ', clickDetail);
          return this.clickService.CreatePageView(clickDetail).pipe(
            tap((response) => this.logService.infoDebug('Effect: saveClickSuccess ', response)),
            map(() => saveClickSuccess()),
            catchError((error: ErrorMessage) => of(saveClickFailure({ error })))
          );
        }
      })
    )
  );

  saveContentClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveContentClick),
      tap((action) => this.logService.info('Effect: saveContentClick ', action)),
      filter((payload) => !!payload.clickTo),
      onlyWhenReady(this.store),
      concatLatestFrom(() => [
        this.store.select(selectSessionID),
        this.store.select(selectTemplateAll())
      ]),
      tap((event) => {
        this.logService.info('saveContentClick received event', event);
      }),

      mergeMap(([payload, sessionId, templateArray]) => {
        const clickDetail: ClickDetail = {
          permaLink: payload.clickTo,
          contextID: payload.context?.contextID ? payload.context.contextID : 0,
          contextTypeID: payload.context?.contextTypeID ? payload.context.contextTypeID : 0,
          contentID: payload.content?.contentID ? payload.content.contentID : 0,
          templateID: templateArray?.[payload.template]?.templateID,
          contentTypeID: payload.content?.contentTypeID ? payload.content.contentTypeID : 0,
          sessionID: sessionId,
          page: payload.template ? payload.template : '',
          queryString: ''
        }
        this.logService.debug('Click detail generated: ', clickDetail);
        return this.clickService.CreatePageView(clickDetail).pipe(
          tap((response) => this.logService.infoDebug('Effect: saveClickSuccess ', response)),
          map(() => saveClickSuccess()),
          catchError((error: ErrorMessage) => of(saveClickFailure({ error })))
        );
      })
    )
  );
  //  save failure
  saveClickFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClickFailure),
      concatLatestFrom(() => this.store.select(selectAuthLoginState)),
      tap((action) => this.logService.info('Effect: saveClickFailure ', action)),
      // Telemetry is fire-and-forget: a failed page-view/click-log must never
      // demote the user (Release 5 / CU-27).
      map(([{ error }, authLoginState]) =>
        manageError(error, authLoginState, ManageErrorSeverity.Background)
      )
    )
  );

  /*
  saveExternalClicks = createEffect(() => this.ngZone.runOutsideAngular(() => {
    // Everything here runs outside of Angular's zone, not affecting stability.
    return fromEvent(this.ssrDocument, 'click').pipe(
      tap((event) => this.logService.info('Effect (outside zone): saveExternalClicks', event)),
      //  we just want PointerEvents, that is a complete touch or a complete click
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      filter((event) => event instanceof PointerEvent),
      //  at the moment, we only are getting click events on A and IMG tags, considering links are only there
      //  this could prove wrong, it has to be verified
      filter((event: PointerEvent) => {
        const htmlEvent: HTMLElement = <HTMLElement>event.target;
        if (
          htmlEvent.tagName &&
          (htmlEvent.tagName.toUpperCase() === 'A' || htmlEvent?.parentElement?.tagName === 'A')
        ) {
          return true;
        } else {
          this.logService.trace('saveExternalclicks: not managed event', htmlEvent.parentElement);
          this.logService.trace(
            'saveExternalclicks: not managed event parent tagname',
            htmlEvent.parentElement?.tagName
              ? htmlEvent.parentElement.tagName
              : 'tagName missing in parentelement'
          );
          return false;
        }
      }),
      map((pointerEvent) => {
        //  to page is in href, if tag is A, or in parent.href, if it's an  image
        let tagError: boolean = false;
        let htmlEvent: HTMLElement = <HTMLElement>pointerEvent.target;
        let clickTo: string = '';
        let contextTypeId: number = 0;
        let contextId: number = 0;

        let clickElement = {
          clickTo: clickTo,
          context: {
            contextID: contextId,
            contextTypeID: contextTypeId
          }
        };
        // if we are here, either tagname is A or parent tagname is A.
        // We need the A element
        if (htmlEvent.tagName.toUpperCase() !== 'A') {
          htmlEvent = htmlEvent.parentElement;
        }

        // get href if possible
        this.logService.debug('saveExternalclicks: found tag A');
        const aTarget: HTMLAnchorElement = <HTMLAnchorElement>htmlEvent;
        // if href is empty it could be a dialog opening. In that case we have href in attribute contenthref
        if (aTarget.href.length > 0) {
          clickTo = aTarget.href;
        } else if (aTarget.getAttribute('contenthref')?.length > 0) {
          clickTo = aTarget.getAttribute('contenthref');
        }

        if (aTarget.getAttribute('contexttypeid')?.length > 0) {
          contextTypeId = Number(aTarget.getAttribute('contexttypeid'));
        }

        if (aTarget.getAttribute('contextid')?.length > 0) {
          contextId = Number(aTarget.getAttribute('contextid'));
        }

        if (clickTo.length === 0) {
          tagError = true;
        }

        if (!tagError) {
          // internal lnks are managed elsewhere
          tagError = !this.isExternalUrl(
            clickTo,
            this.environmentService.clientDomain,
            this.environmentService.contentPath
          );
        }

        clickElement = {
          clickTo: clickTo,
          context: {
            contextID: contextId,
            contextTypeID: contextTypeId
          }
        };
        if (!tagError) {
          return clickElement;
        }
        return undefined;
      }),
      // Now, filter out any clicks we decided to ignore
      filter(clickElement => !!clickElement),

      // Use tap() to dispatch the action by re-entering the zone
      tap((clickElement) => {
        // Step back INSIDE the zone to dispatch the action
        // This ensures NgRx and the rest of your app see the new action.
        this.ngZone.run(() => {
          this.store.dispatch(saveClick({
            clickTo: clickElement.clickTo,
            action: '',
            context: clickElement.context
          }));
        });
      })
    );
  }), { dispatch: false }); // <-- IMPORTANT: Turn off auto-dispatching
*/
  saveExternalClicks = createEffect(() =>
    //  get all click events
    fromEvent(this.ssrDocument, 'click').pipe(
      tap((event) => this.logService.info('Effect: saveExternalClicks', event)),
      //  we just want PointerEvents, that is a complete touch or a complete click
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      filter((event) => event instanceof PointerEvent),
      // Intercept INTERNAL anchors that aren't wired through routerLink (e.g.
      // inline `<a href="...">` inside `[innerHTML]` article body). Take over
      // navigation with the Angular Router so the browser doesn't do a full
      // document load that would re-run createSession() and create a sibling
      // untracked click_master row.
      //
      // Control flow notes:
      //   - Properly wired [routerLink] anchors call preventDefault() in their
      //     own host listener BEFORE this document-level handler runs (event
      //     bubbling). The `defaultPrevented` guard below skips them so we
      //     don't double-route and lose RouterLink's NavigationExtras (state,
      //     relativeTo, preserveFragment, …).
      //   - Anchors we intercept also call preventDefault() (so the browser
      //     doesn't reload), and the next `filter` step rejects events with
      //     `defaultPrevented === true` — so internal clicks never reach the
      //     legacy map / `saveClick$` external-save path. The single
      //     click_detail row for the new SPA nav is written by the existing
      //     ROUTER_NAVIGATED -> savePageViews effect, identical to a properly
      //     wired [routerLink] click.
      //   - External anchors fall through this tap and continue down the
      //     pipeline to dispatch saveClick(...) as before.
      //   - Whether to take over is decided by isRouterNavigable(), NOT by
      //     isExternalUrl(): the latter answers the TRACKING question ("does
      //     this click need an explicit saveClick, or is it already covered
      //     by ROUTER_NAVIGATED -> savePageViews?"), while this tap needs the
      //     NAVIGATION question ("does the Angular router own this URL?").
      //     The two diverge for first-party-but-not-routable URLs: content-CDN
      //     files, /assets/** static files, and — since SPA and backend share
      //     the same domain in prod — /api/** and legacy server paths.
      //     Hijacking those into the Router would land on the wildcard 404.
      //
      // This tap runs BEFORE the legacy <a> filter below so that deeply-nested
      // click targets (e.g. <a><span><strong>x</strong></span></a> or
      // <a><svg>…</svg></a>) are still intercepted. closest('a') walks the
      // full ancestor chain for the anchor, which event.target alone cannot.
      tap((event: PointerEvent) => {
        if (event.defaultPrevented) return;
        // Honor explicit "new tab"/download intent: modifier/middle-click
        // (alt+click is a download trigger in some browsers), target=_blank,
        // download attribute. The _blank keyword is ASCII case-insensitive
        // per the HTML spec.
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const a = this.findAnchorFromEvent(event);
        if (!a) return;
        if (a.target.toLowerCase() === '_blank' || a.hasAttribute('download')) return;

        const raw = a.getAttribute('href');
        if (!raw || /^(mailto:|tel:|javascript:|#)/i.test(raw)) return;

        // a.href resolves to an absolute URL, but for unparseable attribute
        // values (e.g. href="http://" inside [innerHTML] content) the
        // property falls back to the raw string and new URL() throws. A
        // synchronous throw here would error the effect stream and disable
        // click tracking for the session, so malformed hrefs are left to the
        // browser — the same fail-safe direction as the allowlist.
        let url: URL;
        try {
          url = new URL(a.href);
        } catch {
          return;
        }
        if (!this.isRouterNavigable(url)) return;

        event.preventDefault();
        // navigateByUrl resolving false (a guard cancelled) is a normal
        // outcome; a REJECTION (guard/resolver threw) is not. The native
        // click is already suppressed at this point, so on rejection fall
        // back to a full page load — same fail-safe direction as the
        // allowlist — instead of leaving the user on a dead click.
        this.router.navigateByUrl(url.pathname + url.search + url.hash).catch((err) => {
          this.logService.error('saveExternalClicks: router rejected internal navigation, falling back to full load', err);
          this.ssrDocument.location.assign(url.href);
        });
      }),
      //  Filter to clicks that land on (or inside) an <a> element. Uses
      //  findAnchorFromEvent so nested click targets (e.g. <a><span><strong>x)
      //  are detected the same way the upstream interceptor tap does. We also
      //  drop clicks whose default action was already prevented by another
      //  listener (RouterLink, our own internal-anchor tap above, or a custom
      //  handler) — those have been claimed and should not be redundantly
      //  saved as external.
      filter((event: PointerEvent) => {
        if (event.defaultPrevented) return false;
        const a = this.findAnchorFromEvent(event);
        if (a) return true;
        this.logService.trace(
          'saveExternalclicks: click target not inside an anchor',
          event.target
        );
        return false;
      }),
      map((pointerEvent) => {
        //  to page is in href, if tag is A, or in parent.href, if it's an  image
        let tagError: boolean = false;
        let clickTo: string = '';
        let contextTypeId: number = 0;
        let contextId: number = 0;

        let clickElement = {
          clickTo: clickTo,
          context: {
            contextID: contextId,
            contextTypeID: contextTypeId
          }
        };

        // Resolve the anchor via the same helper used by the filter and the
        // upstream interceptor tap; handles arbitrary anchor nesting.
        const aTarget = this.findAnchorFromEvent(pointerEvent);
        if (!aTarget) {
          // Filter should have rejected this — defensive guard only.
          return undefined;
        }

        // get href if possible
        this.logService.debug('saveExternalclicks: found tag A');
        // if href is empty it could be a dialog opening. In that case we have href in attribute contenthref
        if (aTarget.href.length > 0) {
          clickTo = aTarget.href;
        } else if (aTarget.getAttribute('contenthref')?.length > 0) {
          clickTo = aTarget.getAttribute('contenthref');
        }

        if (aTarget.getAttribute('contexttypeid')?.length > 0) {
          contextTypeId = Number(aTarget.getAttribute('contexttypeid'));
        }

        if (aTarget.getAttribute('contextid')?.length > 0) {
          contextId = Number(aTarget.getAttribute('contextid'));
        }

        if (clickTo.length === 0) {
          tagError = true;
        }

        if (!tagError) {
          // internal lnks are managed elsewhere
          tagError = !this.isExternalUrl(
            clickTo,
            this.environmentService.clientDomain,
            this.environmentService.contentPath
          );
        }

        clickElement = {
          clickTo: clickTo,
          context: {
            contextID: contextId,
            contextTypeID: contextTypeId
          }
        };
        if (!tagError) {
          return clickElement;
        }
      }),
      map((clickElement) =>
        saveClick({
          clickTo: clickElement ? clickElement.clickTo : undefined,
          action: '',
          context: clickElement ? clickElement.context : undefined
        })
      )
    )
  );


  savePageViews = createEffect(() =>
    /*		this.router.events.pipe(
          filter((event) => event instanceof NavigationEnd),
      */
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      tap((action: RouterNavigationAction) =>
        this.logService.info('Effect: savePageviews ', action)
      ),
      onlyClient(),
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      map((action: RouterNavigationAction) => action.payload.routerState.url),
      mergeMap((url: string) => [
        setCurrentPage({ currentPage: { page: url } }),
        saveClick({ clickTo: url })
      ])

      /*
          map((event) => event as NavigationEnd),
          tap((event) => this.logService.info('Effect: click savePageviews: ', event)),
          filter((event) => !!event.urlAfterRedirects),
          map((event) => event.urlAfterRedirects),
          map((url: string) => saveClick({ clickTo: url }))
        )
      */
    )
  );

  saveClickLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClickLog),
      // Gate like the other analytics writes: buffer while the session is being rebuilt
      // (sessionID===0 during recovery) instead of firing into the gap and being lost. The
      // buffered log then writes with the correct user + session (read at emit time below).
      onlyWhenOnline(this.store),
      onlyWhenReady(this.store),
      tap((action) => this.logService.info('Effect: saveClickLog ', action)),
      map((payload) => payload.clickLog),
      filter((clickLog) => !!clickLog),
      concatLatestFrom(() => [this.store.select(selectSessionID)]),
      tap((event) => this.logService.info('saveClickLog received event', event)),

      mergeMap(([clickLog, sessionID]) => {
        this.logService.debug('Effect: saveClickLog calling CreateClickLog ');
        clickLog.sessionID = sessionID;
        return this.clickService.CreateClickLog(clickLog).pipe(
          tap((response: ClickLogID) =>
            this.logService.infoDebug('Effect: saveClickLogSuccess ', response)
          ),
          map(() => saveClickLogSuccess()),
          catchError((error: ErrorMessage) => of(saveClickLogFailure({ error })))
        );
      })
    )
  );

  //  save failure
  saveClickLogFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClickLogFailure),
      concatLatestFrom(() => this.store.select(selectAuthLoginState)),
      tap((action) => this.logService.info('Effect: saveClickLogFailure ', action)),
      // Telemetry is fire-and-forget: a failed page-view/click-log must never
      // demote the user (Release 5 / CU-27).
      map(([{ error }, authLoginState]) =>
        manageError(error, authLoginState, ManageErrorSeverity.Background)
      )
    )
  );

  /**
   * Walk up from `event.target` to find the nearest ancestor `<a>` element.
   * Returns null when the click was not inside any anchor.
   *
   * Uses `closest('a')` rather than a single-level `parentElement` walk so it
   * handles arbitrary nesting — e.g. `<a><span><strong>x</strong></span></a>`
   * or `<a><svg><path/></svg></a>` — uniformly. Shared by every step in the
   * saveExternalClicks pipeline that needs to identify the clicked anchor.
   */
  private findAnchorFromEvent(event: Event): HTMLAnchorElement | null {
    const target = event.target as Element | null;
    const anchor = target?.closest?.('a') ?? null;
    // closest('a') also matches SVG <a> elements, whose .target/.href are
    // SVGAnimatedString objects, not strings — calling string methods on them
    // throws and errors the effect stream (killing click tracking for the
    // session). SVG anchors never produced a saveable clickTo anyway, so
    // treat them as "no anchor".
    return anchor instanceof HTMLAnchorElement ? anchor : null;
  }

  /**
   * Distinct from isExternalUrl(), which answers the TRACKING question
   * ("save this click as outbound, or is it already tracked by the
   * ROUTER_NAVIGATED -> savePageViews effect?"). This answers the NAVIGATION
   * question: "does the Angular router own this URL?".
   *
   * Same-origin alone is not enough: in prod the SPA and the backend share
   * https://www.dottnet.it, so /api/**, /assets/**, the content CDN and any
   * legacy server path must keep native browser navigation. A URL is
   * router-navigable only when its first path segment is a route declared in
   * app.routes.ts (all first segments are eager, even for loadChildren —
   * lazy children always mount UNDER their parent segment). Unknown segments
   * fall back to a full page load, where the server decides — never to the
   * SPA's wildcard 404.
   */
  private isRouterNavigable(url: URL): boolean {
    // Compare parsed origins, not string prefixes: a prefix check would
    // misclassify lookalike hosts (https://www.dottnet.it.evil.com/...,
    // https://www.dottnet.it@evil.com/...) as internal.
    if (url.origin !== this.clientOrigin) {
      return false;
    }
    const firstSegment = url.pathname.split('/')[1] ?? '';
    return firstSegment === '' || this.routableSegments.has(firstSegment);
  }

  getClickParametersFromUrl(
    url: string,
    sessionID: number,
    templateAttributes: DottnetTemplate,
    action: string,
    rootDomain: string,
    contextTypeID: number,
    contextID: number
  ): ClickDetail | ErrorMessage {
    let clickDetail: ClickDetail = <ClickDetail>{
      sessionID: 0,
      page: '',
      contextTypeID: 0,
      contextID: 0,
      contentTypeID: 0,
      contentID: 0,
      templateID: 0,
      permaLink: '',
      queryString: ''
    };

    const EXTERNALPAGE: string = 'External page';
    const WRONGPAGE: string = 'Wrong page';

    let errorMessage: ErrorMessage = new ErrorMessage();
    errorMessage.url = url;

    let containerId: string = '';
    let contentId: string = '';
    let isExternalLink: boolean = false;

    clickDetail.sessionID = sessionID;

    if (!url || typeof url !== 'string' || url.length === 0) {
      errorMessage.Code = ErrorType.ErrorUrlWrong;
      errorMessage.messageDN = 'Url not correct. Click detail not generated';
      return errorMessage;
    }

    if (url === '/') {
      clickDetail.page = '/';
      clickDetail.permaLink = '/';
      return clickDetail;
    }

    url = url.replaceAll("'", "''");

    // understand id it's an internal or external link
    if (url.startsWith('http')) {
      // remove host if necessary
      if (url.startsWith(rootDomain)) {
        // internal link. Remove host and go on
        url = url.slice(rootDomain.length);
      } else {
        //  external link manage it separately
        isExternalLink = true;
      }
    }

    // sometimes the first segment is 0 lenght because the link starts with //
    if (url.startsWith('//')) {
      url = url.substring(1);
    }

    // extract url segments and fragments
    // TODO: manage / url
    let cleanUrl: string = '';
    let queryString: string = '';
    let fragment: string = '';
    let tmp: string[] = url.split('?');
    cleanUrl = tmp[0];
    if (tmp.length > 1) {
      queryString = tmp[1];
      tmp = queryString.split('#');
      if (tmp.length > 1) {
        queryString = tmp[0];
        fragment = tmp[1];
      }
    } else {
      tmp = url.split('#');
      if (tmp.length > 1) {
        cleanUrl = tmp[0];
        fragment = tmp[1];
      }
    }

    // Keep the fragment glued to `permaLink`: it's part of the click's
    // semantic target (e.g. `#/step-17` on a landing identifies a specific
    // step — dropping it collapses distinct destinations into one).
    const fragmentSuffix = fragment ? '#' + fragment : '';

    // equal for all links
    clickDetail.queryString = queryString + fragment;

    if (isExternalLink) {
      //  permalink is everything before querystring
      clickDetail.page = EXTERNALPAGE;
      clickDetail.permaLink = cleanUrl + fragmentSuffix;
      clickDetail.templateID = 14; // link
      clickDetail.contextTypeID = contextTypeID;
      clickDetail.contextID = contextID;
      return clickDetail;
    }

    const segments: string[] = cleanUrl.split('/');
    if (segments[0] === '') {
      segments.splice(0, 1);
    }

    // if there is a final / in path there will be an empty final array element
    if (segments[segments.length - 1] === '') {
      segments.splice(segments.length - 1, 1);
    }

    // A root path with a querystring or fragment ('/?utm_source=...', '/#x')
    // leaves no segments; segments[0].toLowerCase() below would throw and
    // error the calling effect stream. Same shape as the bare '/' case above,
    // but with queryString/fragment already extracted.
    if (segments.length === 0) {
      clickDetail.page = '/';
      clickDetail.permaLink = '/' + fragmentSuffix;
      return clickDetail;
    }

    // Templates load synchronously at app init, but this method must never
    // throw (an exception kills the effect stream and all click tracking),
    // so fail soft if the map is not there yet.
    if (!templateAttributes) {
      errorMessage.Code = ErrorType.ErrorUrlWrong;
      errorMessage.messageDN = 'Template map not loaded. Click detail not generated';
      return errorMessage;
    }

    // is it a known template?
    const containerTemplateData: TemplateCt = templateAttributes[segments[0].toLowerCase()];

    const isWrongLink = containerTemplateData === undefined;

    if (isWrongLink) {
      //  permalink is everything before querystring
      clickDetail.page = WRONGPAGE;
      clickDetail.permaLink = cleanUrl + fragmentSuffix;
      clickDetail.templateID = 14; // link
      return clickDetail;
    }

    if (segments.length > 8) {
      errorMessage.Code = ErrorType.ErrorUrlTooManySegments;
      errorMessage.messageDN = 'Url object segments over maximum';
      return errorMessage;
    }

    // action can be PrintPdf, SavePdf
    if (action.length > 0) {
      clickDetail.page = action;
    } else {
      clickDetail.page = segments[0].toLowerCase();
    }

    // here we know that url is well formed and first segment is a known template
    // now we want to know if it's a content page, and we need to check contentid, container and so on, or not
    if (!(containerTemplateData.isContent || containerTemplateData.isContainer)) {
      clickDetail.permaLink = '/' + segments.join('/') + fragmentSuffix;
      clickDetail.templateID = containerTemplateData.templateID;
      clickDetail.contentTypeID = containerTemplateData.contentTypeID;
      return clickDetail;
    }

    // here, first segment is container or content
    // if there are up until 3 segments, they are container, id, title
    // it here are 4 or more segments, we need to check two possible outcomes:
    // 1) container, id, title, but title has some / inside
    // 2) container, id, content, id, title

    // up to 3 segments
    if (segments.length <= 3) {
      clickDetail.permaLink = '/' + segments.join('/') + fragmentSuffix;
      clickDetail.templateID = containerTemplateData.templateID;
      clickDetail.contentTypeID = containerTemplateData.contentTypeID;
      if (containerTemplateData.isContainer) {
        clickDetail.contextTypeID = containerTemplateData.contentTypeID;
      }
      if (segments.length > 1) {
        contentId = segments[1];
        if (contentId.length > 0 && isNumber(contentId)) {
          clickDetail.contentID = Number(contentId);
          if (containerTemplateData.isContainer) {
            clickDetail.contextID = Number(contentId);
          }
        }
      }
      return clickDetail;
    }

    // 4 segments or more
    // check if we are in case 1) or 2) as deswcribed above
    const contentTemplateData: TemplateCt = templateAttributes[segments[2].toLowerCase()];

    containerId = segments[1];
    contentId = segments[3];

    if (contentTemplateData && contentTemplateData.isContent && isNumber(contentId)) {
      // case 2)
      // first segment has to be a container, ad only containers are allowed to show subcontents
      if (!containerTemplateData.isContainer || !isNumber(containerId)) {
        errorMessage.Code = ErrorType.ErrorUrlWrongContainer;
        errorMessage.messageDN = 'Container is not a proper container';
        return errorMessage;
      }

      clickDetail.permaLink = '/' + segments.join('/') + fragmentSuffix;

      clickDetail.contextID = Number(containerId);
      clickDetail.contextTypeID = containerTemplateData.contextTypeID;

      clickDetail.contentID = Number(contentId);
      clickDetail.contentTypeID = contentTemplateData.contentTypeID;

      clickDetail.templateID = contentTemplateData.templateID;

      return clickDetail;
    }

    // case 1).  segments or more, but single content

    clickDetail.permaLink = '/' + segments.join('/') + fragmentSuffix;

    clickDetail.templateID = containerTemplateData.templateID;

    clickDetail.contentTypeID = containerTemplateData.contentTypeID;
    contentId = segments[1];
    if (contentId.length > 0 && isNumber(contentId)) {
      clickDetail.contentID = Number(contentId);
    }

    // console.log('Click detail generated: ', clickDetail);
    return clickDetail;
  }

  isExternalUrl(url: string, rootDomain: string, contentPath: string): boolean {
    if (!url || typeof url !== 'string' || url.length === 0) {
      return false;
    }

    // understand id it's an internal or external link
    if (!url.startsWith('http')) {
      return false;
    }
    // remove host if necessary
    if (url.startsWith(rootDomain)) {
      // internal link. Remove host and go on
      return false;
    }
    if (url.startsWith(contentPath)) {
      // internal link. Remove host and go on
      // content link to a content file
      return false;
    }
    return true;
  }
}
